/**
 * Canonical three-mode lifecycle (Direction #34) presentation surface —
 * label, chip styling, and operator-override resolution shared by every
 * surface that renders an app's mode (Header badge, Apps Tree rows, Login
 * Modal, Sidebar "Active app" footer).
 *
 * Single source of truth so those surfaces NEVER disagree on the rendered
 * lifecycle mode. Read `system_mode` + `operator_mode_override` (carried on
 * BOTH `AppSummaryOut` and `AppDetailOut`) and resolve the operator-visible
 * mode through `effectiveMode`.
 *
 * History — one mode encoding only. A pre-2026-05-07 two-value field
 * `mode ∈ {accelerated, active}` (the OLD names for `learning | classic`;
 * see docs/product/hypothesis-and-signal.md § Migration note) used to live on
 * `AppSummaryOut`. It was hardcoded server-side, could not represent
 * `agent`, and several surfaces mapped it onto lifecycle labels — which is
 * what made the Header pill ("Learning mode") and the Sidebar footer
 * ("Classic mode") disagree for the same app. That field is retired; do
 * NOT reintroduce a second mode encoding.
 */

import type { AppOperatorModeOverride, AppSystemMode } from "@/types";

/** Operator-facing label for each lifecycle mode. */
export const systemModeLabel: Record<AppSystemMode, string> = {
  learning: "Learning",
  classic: "Classic",
  agent: "Agent",
};

/** Tailwind chip background + text token pair per lifecycle mode. */
export const systemModeChipClass: Record<AppSystemMode, string> = {
  learning: "bg-sky-50 text-sky-700",
  classic: "bg-slate-50 text-slate-700",
  agent: "bg-indigo-50 text-indigo-700",
};

/**
 * Resolve the operator-visible lifecycle mode. An active operator override
 * wins over the auto-progressed `system_mode`; `follow_system` defers to
 * it. Mirrors the server-side effective-mode resolution.
 */
export function effectiveMode(
  systemMode: AppSystemMode,
  override: AppOperatorModeOverride,
): AppSystemMode {
  if (override === "force_agent") return "agent";
  if (override === "force_classic") return "classic";
  return systemMode;
}
